import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import Button from '../../elements/Button';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TableBody, TableCell } from '../../Table';
import { Table, TableHead, TableRow, TableHeader } from '../../Table';
import { TableWrapper } from '../../Table';
import { CompanyResponse, editLearningPlans } from '../../../api/admin';
import { SmallTooltip } from '../../Tooltip';
import LearningPlanEditableCell, {
  OptionType,
  getLearningPlanOptions,
} from '../Cells/LearningPlanEditableCell';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';
import { toast } from 'react-toastify';
import { Dropdown } from '../../elements/Dropdown';
import useAuth from '../../../hooks/use-auth';
import { useMutation } from 'react-query';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

export type MinimalLearningPlanUnit = {
  type: LEARNING_PLAN_UNIT_TYPE;
  slug: string;
};

type LearnerWithPlan = {
  id: number;
  name: string;
  email: string;
  originalLearningPlan: MinimalLearningPlanUnit[];
  newLearningPlan: MinimalLearningPlanUnit[];
};

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  button {
    margin: 0;
    width: 120px;
  }

  > div:first-child {
    width: 500px;
  }
`;
const BulkAddWidget = ({
  modules,
  projects,
  onAddUnit,
}: {
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  onAddUnit: (unit: MinimalLearningPlanUnit) => void;
}) => {
  const options = getLearningPlanOptions(modules, projects);
  const [selectedOption, setSelectedOption] =
    React.useState<OptionType | null>(null);

  const onSelectOption = (option: OptionType) => {
    const isDraftOrDeprecated =
      option.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
        ? modules.find(
            (m) =>
              m.contentSlug === option.slug && (m.isDraft || m.isDeprecated)
          )
        : projects.find((p) => p.slug === option.slug && p.isDraft);
    if (isDraftOrDeprecated) {
      toast.error(
        'You cannot add a draft or deprecated unit to the learning plan'
      );
      return;
    }
    setSelectedOption(option);
  };
  return (
    <ActionRow>
      <Dropdown
        options={options}
        onChange={onSelectOption}
        value={selectedOption}
        isValidNewOption={() => false}
        placeholder="Select a unit to add to all learners"
        formatOptionLabel={(option) => option.dropdownLabel}
        getOptionLabel={(option) => option.dropdownLabel}
      />
      <Button
        label="Add"
        type="secondary"
        faIcon="fa-solid fa-plus"
        showIconBeforeLabel
        isDisabled={!selectedOption}
        onClick={() => {
          if (selectedOption) {
            onAddUnit(selectedOption);
            setSelectedOption(null);
          }
        }}
      />
    </ActionRow>
  );
};

const BulkRemoveWidget = ({
  modules,
  projects,
  onRemoveUnit,
}: {
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  onRemoveUnit: (unit: MinimalLearningPlanUnit) => void;
}) => {
  const options = getLearningPlanOptions(modules, projects);
  const [selectedOption, setSelectedOption] =
    React.useState<OptionType | null>(null);
  return (
    <ActionRow>
      <Dropdown
        options={options}
        onChange={setSelectedOption}
        value={selectedOption}
        placeholder="Select a unit to remove from all learners"
        getOptionLabel={(option) => option.dropdownLabel}
      />
      <Button
        label="Remove"
        type="secondary"
        faIcon="fa-solid fa-minus"
        showIconBeforeLabel
        isDisabled={!selectedOption}
        onClick={() => {
          if (selectedOption) {
            onRemoveUnit(selectedOption);
            setSelectedOption(null);
          }
        }}
      />
    </ActionRow>
  );
};

const columnHelper = createColumnHelper<LearnerWithPlan>();

export default function EditLearnersLearningPlanModal({
  isOpen,
  onClose,
  companyId,
  selectedLearnerIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  selectedLearnerIds: number[];
}) {
  const {
    data,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(companyId);
  const [newLearners, setNewLearners] = React.useState<LearnerWithPlan[]>([]);
  const { getTokenSilently } = useAuth();

  const editPlanMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      return editLearningPlans(token, {
        companyId,
        learners: newLearners.map((learner) => ({
          id: learner.id,
          learningPlan: learner.newLearningPlan.map((unit) => ({
            type: unit.type,
            slug: unit.slug,
            reasoning: '',
          })),
        })),
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Learning plans updated successfully');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update learning plans'
      );
    },
  });

  React.useEffect(() => {
    const learners: LearnerWithPlan[] = selectedLearnerIds
      .map((id) => data?.learners.find((learner) => learner.id === id))
      .filter(
        (learner): learner is CompanyResponse['learners'][number] => !!learner
      )
      .map((learner) => ({
        id: learner.id,
        name: learner.name,
        email: learner.email,
        originalLearningPlan:
          learner.learningPlan?.map((unit) => ({
            type: unit.type,
            slug:
              unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
                ? unit.content.slug
                : unit.slug,
          })) ?? [],
        newLearningPlan:
          learner.learningPlan?.map((unit) => ({
            type: unit.type,
            slug:
              unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
                ? unit.content.slug
                : unit.slug,
          })) ?? [],
      }));
    setNewLearners(learners);
  }, [data, selectedLearnerIds]);

  const handleUpdateData = (value: {
    learnerId: number;
    newLearningPlan: MinimalLearningPlanUnit[];
  }) => {
    const oldPlan = newLearners.find(
      (l) => l.id === value.learnerId
    )?.originalLearningPlan;
    const newPlan = value.newLearningPlan;

    const additions = newPlan.filter(
      (unit) => !oldPlan?.find((l) => l.slug === unit.slug)
    );
    const isDraftOrDeprecatedUnit = (unit: MinimalLearningPlanUnit) => {
      if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
        return (data?.modules ?? []).find(
          (m) => m.contentSlug === unit.slug && (m.isDraft || m.isDeprecated)
        );
      }
      return (data?.projects ?? []).find(
        (p) => p.slug === unit.slug && p.isDraft
      );
    };

    const hasDraftOrDeprecatedAddition = additions.some(
      isDraftOrDeprecatedUnit
    );

    if (hasDraftOrDeprecatedAddition) {
      toast.error(
        'You cannot add a draft or deprecated unit to the learning plan'
      );
      return;
    }

    const updatedLearners = newLearners.map((learner) => {
      if (learner.id === value.learnerId) {
        return {
          ...learner,
          newLearningPlan: value.newLearningPlan,
        };
      }
      return learner;
    });
    setNewLearners(updatedLearners);
  };

  const onBulkAddUnit = (unit: MinimalLearningPlanUnit) => {
    setNewLearners(
      newLearners.map((learner) => {
        const hasUnit = learner.newLearningPlan.some(
          (existingUnit) => existingUnit.slug === unit.slug
        );
        if (hasUnit) {
          return learner;
        }
        return {
          ...learner,
          newLearningPlan: [...learner.newLearningPlan, unit],
        };
      })
    );
  };

  const onBulkRemoveUnit = (unit: MinimalLearningPlanUnit) => {
    setNewLearners(
      newLearners.map((learner) => ({
        ...learner,
        newLearningPlan: learner.newLearningPlan.filter(
          (existingUnit) => existingUnit.slug !== unit.slug
        ),
      }))
    );
  };

  const hasChanges = newLearners.some((learner) => {
    const newPlanSlugs = learner.newLearningPlan
      .map((unit) => unit.slug)
      .sort();
    const originalPlanSlugs = learner.originalLearningPlan
      .map((unit) => unit.slug)
      .sort();
    return JSON.stringify(newPlanSlugs) !== JSON.stringify(originalPlanSlugs);
  });
  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <Row>
        <Header>Edit {data?.name} Learning Plans</Header>
        <CloseButton onClick={onClose} />
      </Row>
      <Content>
        <PSmall style={{ marginTop: '32px' }}>
          You have selected {selectedLearnerIds.length} learners to edit their
          learning plans.
        </PSmall>
        <BulkAddWidget
          modules={data?.modules ?? []}
          projects={data?.projects ?? []}
          onAddUnit={onBulkAddUnit}
        />
        <BulkRemoveWidget
          modules={data?.modules ?? []}
          projects={data?.projects ?? []}
          onRemoveUnit={onBulkRemoveUnit}
        />
        <EditLearnersTable
          data={newLearners}
          modules={data?.modules ?? []}
          projects={data?.projects ?? []}
          onUpdateData={handleUpdateData}
        />
        <Row>
          <Button
            label="Save Learning Plans"
            showIconBeforeLabel
            faIcon="fa-solid fa-check"
            type="primary"
            isDisabled={!hasChanges}
            onClick={() => {
              editPlanMutation.mutate();
            }}
            isLoading={editPlanMutation.isLoading || isLoadingCompany}
          />
        </Row>
      </Content>
    </Wrapper>
  );
}

function PlanChangeSummary({
  oldPlan,
  newPlan,
  modules,
  projects,
}: {
  oldPlan: MinimalLearningPlanUnit[];
  newPlan: MinimalLearningPlanUnit[];
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  learnerId: number;
}) {
  const additions = newPlan.filter(
    (unit) => !oldPlan.find((l) => l.slug === unit.slug)
  );

  const removals = oldPlan.filter(
    (unit) => !newPlan.find((l) => l.slug === unit.slug)
  );

  if (additions.length === 0 && removals.length === 0) {
    return <div>No changes</div>;
  }

  const getTitle = (unit: MinimalLearningPlanUnit) => {
    if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
      const module = modules.find((m) => m.contentSlug === unit.slug);
      return module?.contentTitle || unit.slug;
    } else {
      const project = projects.find((p) => p.slug === unit.slug);
      return project?.title || unit.slug;
    }
  };

  return (
    <div style={{ width: 'max-content' }}>
      {additions.length > 0 && (
        <div>
          <b>Added</b>{' '}
          {additions.map((unit) => `"${getTitle(unit)}"`).join(', ')}
        </div>
      )}
      {removals.length > 0 && (
        <div>
          <b>Removed</b>{' '}
          {removals.map((unit) => `"${getTitle(unit)}"`).join(', ')}
        </div>
      )}
    </div>
  );
}

const TWrapper = styled(TableWrapper)`
  overflow-x: auto;
  table {
    min-width: 70vw;
  }
`;

const EditLearnersTable = ({
  data,
  modules,
  projects,
  onUpdateData,
}: {
  data: LearnerWithPlan[];
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  onUpdateData: (value: {
    learnerId: number;
    newLearningPlan: MinimalLearningPlanUnit[];
  }) => void;
}) => {
  const columns = [
    columnHelper.display({
      id: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return (
          <>
            <div className={`learner-${row.original.id}`}>
              {row.original.name ?? 'N/A'}
            </div>
            <SmallTooltip anchorSelect={`.learner-${row.original.id}`}>
              {row.original.email}
            </SmallTooltip>
          </>
        );
      },
      meta: {
        styles: {
          minWidth: '250px',
          maxWidth: '250px',
        },
      },
    }),
    columnHelper.display({
      id: 'learningPlan',
      header: 'Learning Plan',
      cell: ({ row }) => {
        return (
          <LearningPlanEditableCell
            learnerId={row.original.id}
            modules={modules}
            projects={projects}
            newLearningPlan={row.original.newLearningPlan}
            onChange={(newLearningPlan) => {
              onUpdateData({
                learnerId: row.original.id,
                newLearningPlan,
              });
            }}
          />
        );
      },
      meta: {
        styles: {
          minWidth: '500px',
          overflowX: 'auto',
          width: '100%',
          maxWidth: '600px',
          padding: '4px 12px',
        },
      },
    }),
    columnHelper.display({
      id: 'changesSummary',
      header: 'Changes Summary',
      cell: ({ row }) => {
        return (
          <PlanChangeSummary
            learnerId={row.original.id}
            modules={modules}
            projects={projects}
            oldPlan={row.original.originalLearningPlan}
            newPlan={row.original.newLearningPlan}
          />
        );
      },
      meta: {
        styles: {
          minWidth: '300px',
          maxWidth: '300px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
        },
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  return (
    <TWrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} meta={header.column.columnDef.meta}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} isSelected={row.getIsSelected()}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} meta={cell.column.columnDef.meta}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TWrapper>
  );
};
